import type { Ref } from 'vue'
import type { WebChatMessage, MessageAttachment, MessageCard, WorkflowRequest, WorkflowRequestField } from '~/types/web-chat'
import { useDocumentsStore } from '~/stores/documents'
import { useWorkflowRunsStore } from '~/stores/workflowRuns'
import { asRunCompleteness, isDraft } from '~~/shared/workflows/completeness'
import { isBlankValue, workflowFieldsMeta } from '~/utils/workflowFields'
import { requestFieldComplete } from '~/utils/workflowRunItems'
import { sanitizeUpstreamError } from '~/utils/toolRunMessages'
import type { ApiFetch } from '~/composables/useApi'
import type { WorkflowDraftPost } from '~/composables/web-chat/useWorkflowDraftSync'
import type { WorkflowDraftResult } from '~~/shared/workflows/workflowRun'
import { useDocumentConversion } from '~/composables/web-chat/useDocumentConversion'
import { planAllows } from '~/composables/usePlanFeature'
import type { WorkflowProposalStatus } from '~~/shared/workflows/workflowProposal'

/**
 * Starting, declining and handing off a workflow run proposed in chat (MAR-437).
 *
 * The card owns the VALUES (the user edits them in place); this owns what happens to them. A proposed
 * document CONVERSION is a tool run, not a workflow run, and lives in `useDocumentConversion`; this
 * composes it so the store's surface (`acceptConversion`) did not move (MAR-948).
 */
export interface WorkflowActionsDeps {
  messages: Ref<WebChatMessage[]>
  isStreaming: Ref<boolean>
  apiFetch: ApiFetch
  sendMessage: (content: string, attachments?: MessageAttachment[], options?: { displayContent?: string; hiddenFromUi?: boolean; persist?: boolean }) => Promise<unknown>
  /** The conversation these runs belong to, so the Activity row can link back to it (MAR-109). */
  conversationId?: Ref<string | null>
  router: { push: (to: any) => void }
  /** Where the user is now — an auto-open must not yank them off another page. */
  currentPath: Ref<string>
  /** Opens the split document preview beside chat. Owned by the `web-chat` store, not
   *  `usePanelLayout` — it is conversation state (cleared on switch/new-chat), not shell state. */
  openDocPreview: (documentId: string, name: string) => void
  /** The chat's case rung — a conversion's output files into it. */
  activeCaseId: Ref<string | null>
  /** The one `/draft` request (`useWorkflowDraftSync`), which fills in the scope itself. */
  postWorkflowDraft: (workflowId: string, body: WorkflowDraftPost) => Promise<WorkflowDraftResult>
  /** Sends any live edit still inside the debounce window. */
  flushWorkflowDraft: () => Promise<void>
  /** Saves an UNCONFIRMED proposal onto its tool call (`useWorkflowDraftSync`, MAR-988) — a Cancelar
   *  is recorded there, so a reload does not offer the declined proposal again. */
  saveWorkflowProposal: (request: WorkflowRequest, status?: WorkflowProposalStatus) => Promise<void>
}

export function useWorkflowActions(deps: WorkflowActionsDeps) {
  const {
    messages, isStreaming, apiFetch, sendMessage, conversationId, router, currentPath,
    openDocPreview, activeCaseId, postWorkflowDraft, flushWorkflowDraft,
  } = deps

  // Safe here: the only caller is the `web-chat` store's own setup, which already calls `useI18n()`.
  const { t } = useI18n()

  function refreshDocumentBrowser() {
    try {
      useDocumentsStore().refreshAll()
    } catch (err) {
      console.warn('[webChatStore] Failed to auto-refresh document browser:', err)
    }
  }

  const { acceptConversion } = useDocumentConversion({
    messages, apiFetch, sendMessage, conversationId, currentPath, openDocPreview, activeCaseId, refreshDocumentBrowser,
  })

  /**
   * The newest workflow card still awaiting a decision, WITH its message.
   *
   * The message id travels onto the run so a reload can put the status card back on the right
   * message — a thread can hold several runs, and matching by order would guess.
   */
  function pendingWorkflow(): { msg: WebChatMessage; req: WorkflowRequest } | null {
    const msg = [...messages.value].reverse().find(
      m => m.role === 'assistant' && m.card?.toolId === 'run_workflow' && m.card.request.status === 'pending',
    )
    if (!msg) return null
    return { msg, req: (msg.card as Extract<MessageCard, { toolId: 'run_workflow' }>).request }
  }

  /**
   * The values the run will receive, read off the card rather than off the proposal.
   *
   * This is the point of the whole design: what the user SEES is what is sent. A field the user
   * corrected, cleared or filled wins over whatever the agent originally proposed. `field.value` is
   * now the canonical value regardless of which surface wrote it (MAR-437 follow-up) — a party
   * picked from the context rail writes through `webChat.syncWorkflowField`, which mutates this
   * SAME `req.fields` array, not a separate mirror.
   */
  function collectValues(req: WorkflowRequest): Record<string, any> {
    const values: Record<string, any> = {}
    for (const field of req.fields) {
      if (isBlankValue(field.value)) continue
      values[field.key] = field.value
    }
    return values
  }

  /**
   * Open the produced document in the split preview beside chat (MAR-437 follow-up), instead of
   * either auto-downloading it or navigating away to `/app/docs/:id` — both of the old behaviours
   * pulled the user out of the conversation (a silent file in Downloads, or a full-page jump) to
   * show them something the chat itself can show. The manual "Descargar" affordance on the card and
   * on the preview panel's own header still saves the actual file.
   *
   * Auto-opens ONLY for a single deliverable, and only while the user is still on this conversation.
   * Picking one of several would be a guess, and pulling someone off a page they navigated to
   * themselves — minutes after they started a run — is worse than making them click.
   */
  function openProducedDocument(req: WorkflowRequest, startedOnPath: string) {
    const withDocs = (req.outputs ?? []).filter(o => o.documentId)
    // Both refusals are deliberate, and both used to be invisible — "the document did not open" read
    // as a bug in every report of it. Saying which rule applied is the difference between a
    // behaviour and a fault.
    if (withDocs.length !== 1) {
      console.info(`[webChat] run_workflow: not auto-opening — the run produced ${withDocs.length} documents, and picking one would be a guess.`)
      return
    }
    if (currentPath.value !== startedOnPath) {
      console.info('[webChat] run_workflow: not auto-opening — the user navigated away from the conversation that started the run.')
      return
    }
    openDocPreview(withDocs[0]!.documentId!, withDocs[0]!.name)
  }

  /**
   * Required fields the user has not filled — the blanks the run would leave in the document.
   *
   * Computed HERE, off the same `req.fields` the card renders, rather than passed in from the click,
   * so this can never disagree with what the card itself showed the user before they pressed the
   * button.
   *
   * EVERY blank required field, including the ones the card cannot render a control for — a missing
   * document picker disables the button outright (see `canRun` in `WorkflowCard.vue`), so wherever a
   * run can actually start the count only ever includes fillable blanks.
   *
   * "Blank" is the run form's rule (`requestFieldComplete`, MAR-948) — the same one the card's own
   * "missing" list uses — so an unticked required checkbox makes this a draft, and a select with no
   * options (#862) does not.
   */
  function blankRequired(req: WorkflowRequest): WorkflowRequestField[] {
    return req.fields.filter(f => f.required && !requestFieldComplete(f))
  }

  /**
   * Start the proposed run. `completeness` is `draft` whenever a required field is still blank,
   * `strict` otherwise — the run's only decision now, since generating and opening the result in the
   * split preview is the one thing this button does (MAR-437 follow-up: "Descargar borrador" used to
   * be a second button forcing a draft AND an automatic download; both are gone — the preview panel's
   * own header has a Download button once the result is showing).
   */
  async function acceptWorkflow() {
    const pending = pendingWorkflow()

    // ── EVERY REFUSAL SAYS WHY ──────────────────────────────────────────────────────────────────
    //
    // These early returns used to be silent, and a silent return from a button handler is
    // indistinguishable from a broken button — which is exactly how it was reported. Whatever the
    // cause, the console now names it.
    //
    // NOT gated on `isStreaming`, which was one of those silent causes: the card resumes the paused
    // tool call IMMEDIATELY, so the agent is usually still writing its follow-up while the buttons
    // are on screen, and every click in that window did nothing. Starting a run is an independent
    // POST; it does not touch the stream, and the only message it sends goes out when the run
    // SETTLES, minutes later.
    if (!pending) {
      console.warn('[webChat] run_workflow: no pending workflow card to act on.')
      return
    }
    if (!pending.req.workflowId) {
      console.warn('[webChat] run_workflow: the card has no workflowId — the definition lookup failed.')
      return
    }
    const { msg, req } = pending
    const blanks = blankRequired(req)
    // Frozen at launch: the fields stay editable, and a count that kept moving afterwards would
    // describe a proposal nobody ran.
    req.blanks = blanks.length
    req.completeness = blanks.length > 0 ? 'draft' : 'strict'
    req.status = 'running'
    const startedOnPath = currentPath.value

    try {
      // A draft is scoped to ONE conversation (`upsertWorkflowDraft`'s lookup must never widen), so
      // `/draft` requires a conversation id and 400s on `null` (MAR-948). Refuse here, through the
      // same catch every other failure takes, instead of sending a request the server must reject.
      const draftConversationId = conversationId?.value
      if (!draftConversationId) throw new Error('run_workflow: no conversation to scope the draft to')

      // Flush any edit still sitting in the debounce window BEFORE sending the confirm request
      // (MAR-437 follow-up). Not for correctness — `collectValues(req)` below already reads the
      // live local values, debounced or not — but so nothing is left pending to fire a redundant,
      // already-stale PATCH a few hundred ms after the run this click is about to finalize.
      await flushWorkflowDraft()

      // `/draft` (MAR-437 follow-up), not `/run`: one run+document per (conversation, workflow), so a
      // second confirm of the same negotiation regenerates that document rather than creating a
      // second, disconnected run. THIS click is what creates it the first time — a proposal writes
      // nothing before it (MAR-988, CHAT-2.07). It executes SYNCHRONOUSLY, so there is no queue to
      // poll — the response IS the settled result.
      // Through `postWorkflowDraft`, which adds the chat's case/client rung to the body itself.
      const draftResult = await postWorkflowDraft(req.workflowId, {
        values: collectValues(req),
        // A TYPED TOP-LEVEL FIELD, not a reserved key inside `inputValues`: it used to travel as
        // `__draft: true` smuggled among the values, indistinguishable from a workflow input called
        // `__draft`. `draft` tells the pipeline that substituting NOTHING is the expected outcome
        // rather than the failure "No hay nada que rellenar en esta plantilla".
        completeness: req.completeness,
        conversationId: draftConversationId,
        // The message this card belongs to, so a reload rebuilds the status card exactly.
        conversationMessageId: msg.id,
      })
      req.runId = draftResult.runId
      // The negotiation now HAS a document, so a later edit on the card or the rail regenerates it in
      // place (`queueWorkflowDraftSync` posts only once `draftRunId` is set — MAR-988).
      req.draftRunId = draftResult.runId
      req.itemId = draftResult.itemId

      const runs = useWorkflowRunsStore()
      // `completeness` travels to the Activity Centre too, so the bell can say "Borrador listo"
      // rather than "Terminado" for a document that still has blanks in it. Tracked even though the
      // result is already settled — this is what makes the run show up in `/api/runs` history and
      // the Activity Center consistently with every other run.
      runs.trackRun({
        id: draftResult.runId,
        workflowName: req.workflowName ?? null,
        conversationId: conversationId?.value ?? null,
        completeness: req.completeness,
        blanks: req.blanks,
      })

      const final = await apiFetch<any>(`/api/runs/${draftResult.runId}`)
      req.status = 'completed'
      req.outputs = final?.outputs ?? []
      // The rail reads THIS SAME `req` object via `activeWorkflowRequest` (MAR-437 follow-up) — the
      // values that produced this document stay visible as a summary rather than clearing, until a
      // genuinely new proposal replaces them. Setting `req.status` above is the whole mechanism now;
      // there is no separate draft object to also mark complete.
      refreshDocumentBrowser()
      // Opens the result in the split preview beside chat (MAR-437 follow-up) — the user no
      // longer has to trust an invisible download or get yanked to a full page to see what came
      // out. Saving the actual file is a deliberate click on the preview panel's own header.
      openProducedDocument(req, startedOnPath)
      await sendMessage(
        // English per services/agent-web-chat/AGENTS.md — every user-turn instruction to the model is English,
        // never the reply language. Neutral on the outcome, deliberately: "completed
        // successfully" is the phrase the agent used to parrot back as its own claim, including
        // for runs that produced nothing (MAR-97/MAR-39). The draft sentence exists for the same
        // reason: left to itself the agent reports a draft as a finished document (MAR-39 again).
        //
        // "PLAIN TEXT ONLY, no tool call" is the actual fix for a real duplicate: the workflow
        // card already shown above this message has its own working download button, and the
        // model was independently deciding to ALSO call `download_documents`, rendering a second,
        // redundant confirm card offering the identical file. A tool-description warning alone
        // was not reliable enough — this is the instruction the model is actually reacting to.
        `[The workflow run finished. Run id: ${draftResult.runId}. Result: ${JSON.stringify(final?.final_output || {})}.`
        + (isDraft(req.completeness)
          ? ` It ran as a DRAFT and ${req.blanks ?? 0} required fields were left blank, so the document still has unfilled placeholders. Say so explicitly: this is a draft to finish in Word, not a final document.`
          : '')
        + ' Review the result and tell the user what was actually produced — never claim anything the result does not show. Reply with PLAIN TEXT ONLY: do not call any tool for this, and specifically do not call download_documents — the card already shown to the user has its own download button for this exact file, and a second card offering it again is a visible duplicate bug, not a second option.]',
        undefined,
        { hiddenFromUi: true, persist: false },
      )
    } catch (err: any) {
      req.status = 'failed'
      // Same boundary as `acceptConversion`'s catch (#807): the caught exception's own `.message`
      // can be an upstream diagnostic verbatim, so it is never shown or handed to the model — only
      // the console. This request carries no coded `ToolRunErrorCode` at all, so it is always
      // `code: 'internal'`.
      const { userMessage, modelReason, diagnostic } = sanitizeUpstreamError(t, err, req.workflowName)
      req.error = userMessage
      console.error('[webChat] run_workflow: request failed:', diagnostic)
      await sendMessage(
        `[The workflow run could not be started. Reason: ${modelReason} Tell the user what happened,`
        + ' in your own words; do not invent another cause and do not retry it yourself.'
        + ' Reply with PLAIN TEXT ONLY: do not call run_workflow again.]',
        undefined,
        { hiddenFromUi: true, persist: false },
      )
    }
  }

  /**
   * Hand the proposal to the full run form, values and all.
   *
   * The chat card deliberately cannot render a document picker or a batch table; this is the way out
   * for anything it cannot ask for. Navigating there makes the page content, which docks the chat to
   * the right panel — so the form and the conversation sit side by side.
   */
  function openWorkflowForm() {
    const req = pendingWorkflow()?.req
    if (!req?.workflowId) {
      console.warn('[webChat] open form: no pending workflow card, or it has no workflowId.')
      return
    }
    useWorkflowRunsStore().setRunPrefill(req.workflowId, collectValues(req))
    // Left `pending`: the user may come back and run it from the card instead. Settling it here
    // would strand the card as decided while nothing had actually been decided.
    router.push({ path: `/app/workflows/${req.workflowId}/run` })
  }

  async function declineWorkflow() {
    const req = pendingWorkflow()?.req
    // Same rule as `acceptWorkflow`: a button that does nothing has to say why. Cancelling DOES
    // stay gated on the stream, unlike accepting — it sends a visible user turn, and posting one
    // mid-stream interleaves it with the agent's own reply.
    if (!req) {
      console.warn('[webChat] run_workflow: nothing to decline — no pending workflow card.')
      return
    }
    if (isStreaming.value) {
      console.warn('[webChat] run_workflow: not declining while the assistant is still streaming; the cancellation message would interleave with its reply.')
      return
    }
    req.status = 'declined'
    // An unconfirmed proposal has no run to cancel; the decline is recorded on its tool call, which
    // is what a reload rebuilds the card from (MAR-988). A confirmed one keeps its run and document.
    if (!req.draftRunId) await deps.saveWorkflowProposal(req, 'declined')
    await sendMessage(
      '[Ejecución de flujo de trabajo cancelada por el usuario. No realices ninguna otra acción.]',
      undefined,
      { displayContent: 'Cancelar ejecución de automatización' },
    )
  }

  /**
   * Put the run cards back after a reload.
   *
   * `msg.card` is in-memory by design — the tool call itself persists, the card does not — so before
   * this a finished run vanished from the thread the moment the page reloaded, and the only trace
   * was an Activity row. The runs carry the message they came from (`conversation_message_id`), so
   * this is an exact restore rather than a match by ordering, which would put the wrong card on the
   * wrong message in a thread that ran two workflows.
   *
   * Rebuilt cards are EDITABLE (`status: 'pending'`), not a static status display (MAR-437
   * follow-up) — a workflow-linked document stays reopenable and regenerable from chat regardless of
   * whether it has since finished (decision 7): the same fields, the same values, the same "Generar
   * borrador" button. Only a CONFIRMED negotiation has a run to rebuild from — a proposal persists
   * nothing before its confirm (MAR-988), so an unconfirmed card has no row here; it comes back from
   * its persisted call instead (`rehydrateConfirmCards`), and a run found here replaces that card.
   *
   * Iterated OLDEST-first so the LAST card attached — and so `webChat.activeWorkflowRequest`'s scan,
   * which finds the newest `run_workflow` card — lands on the most recent run, even though
   * `/api/runs` itself returns newest first.
   *
   * Best-effort and silent: a thread with no runs costs one query, and a failure leaves the
   * transcript exactly as it is today.
   */
  async function rehydrateWorkflowCards(id: string) {
    try {
      // `/api/runs` is behind the `workflows` gate; a plan without it has no runs to restore and must
      // not be refused on every conversation open (MAR-980).
      if (!(await planAllows('workflows'))) return
      const runs = await apiFetch<any[]>(`/api/runs?conversationId=${encodeURIComponent(id)}&limit=25`)
      if (!runs?.length) return

      for (const run of [...runs].reverse()) {
        // A user-declined negotiation stays declined — resurrecting it as an editable card would be
        // a second chance to run something the user explicitly said no to.
        if (!run?.conversation_message_id || run.status === 'cancelled') continue
        // The exact match is the correct one whenever it exists — see the header comment on why
        // this isn't matched by ordering. But an id that names no message in this thread DOES
        // happen (observed live: a run's `conversation_message_id` pointing at nothing persisted,
        // likely a client hiccup during that turn's own send) and used to mean the card silently
        // never came back — worse than an imprecise match in a thread that ran only one workflow,
        // which is the overwhelmingly common case. So: exact match first; if that finds nothing,
        // fall back to the newest assistant message still without a card, rather than showing
        // nothing at all for a run that genuinely exists.
        const msg = messages.value.find(m => m.id === run.conversation_message_id)
          ?? [...messages.value].reverse().find(m => m.role === 'assistant' && !m.card)
        // Only ever ADD a card. A card already in the thread is live state the user may be editing;
        // replacing it with a database row would discard their edits mid-turn. The one exception is a
        // proposal card `rehydrateConfirmCards` rebuilt from its CALL (MAR-988): a run for it means it
        // was confirmed, and the run — its document, its status — is the truth about it now.
        const fromCall = msg?.card?.toolId === 'run_workflow' && msg.card.request.restoredFromCall === true
        if (!msg || (msg.card && !fromCall)) continue

        if (run.status === 'pending' || run.status === 'executing') {
          // A genuinely still-running (queued) execution — the run form / trigger path, which this
          // negotiation flow does not produce. Status display, not an editable form: there is
          // nothing to edit until it settles.
          msg.card = {
            toolId: 'run_workflow',
            request: {
              workflowId: run.definition_id ?? '',
              workflowName: run.workflow_name ?? '',
              reason: '',
              status: 'running',
              fields: [],
              loading: false,
              runId: run.id,
              outputs: run.outputs ?? [],
              completeness: asRunCompleteness(run.completeness),
            },
          }
          continue
        }

        await seedRehydratedWorkflowCard(msg, run)
      }
    } catch (err) {
      console.warn('[webChatStore] could not rehydrate workflow cards:', err)
    }
  }

  /**
   * Rebuild ONE card as an editable resume of its run — the fields, the values, the ability to keep
   * editing and re-generate. Nothing else to seed: `webChat.activeWorkflowRequest` finds this card by
   * scanning `messages.value` once it's attached below, the same way it finds any other.
   */
  async function seedRehydratedWorkflowCard(msg: WebChatMessage, run: any) {
    try {
      const workflow = await apiFetch<any>(`/api/workflows/${run.definition_id}`)
      const fieldsMeta = workflowFieldsMeta(workflow)
      const values = run.input_values ?? {}

      msg.card = {
        toolId: 'run_workflow',
        request: {
          workflowId: run.definition_id ?? '',
          workflowName: run.workflow_name ?? workflow?.name ?? '',
          reason: '',
          status: 'pending',
          notices: workflow?.notices ?? undefined,
          conversationMessageId: run.conversation_message_id,
          fields: fieldsMeta.map(meta => ({
            ...meta,
            value: values[meta.key],
            prefilled: values[meta.key] !== undefined,
          })),
          loading: false,
          draftRunId: run.id,
          itemId: run.outputs?.find((o: any) => o.documentId)?.documentId ?? null,
          // Read defensively from the row: a run persisted before the column existed reports
          // nothing and reads as `strict`, which is what it was.
          completeness: asRunCompleteness(run.completeness),
        },
      }
    } catch (err) {
      console.warn('[webChatStore] could not rehydrate an editable workflow card:', err)
    }
  }

  return {
    acceptWorkflow,
    acceptConversion,
    declineWorkflow,
    openWorkflowForm,
    rehydrateWorkflowCards,
  }
}
